import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Mail, Download, Loader2, Plus, TrendingUp, FileText, ShieldCheck,
  UploadCloud, Trash2, IdCard, ReceiptText, FileBadge, CalendarClock, Trash, Pencil, History,
  CheckCircle2, AlertTriangle, UserCog,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUREAU_COLORS, STATUS_COLORS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EDIT_STATUSES = ["New Intake", "Active Disputes", "Under Bureau Review", "Resolved / Blocked"];

const BUREAUS = ["Equifax", "Experian", "TransUnion"];
const DOC_TYPES = [
  { key: "ssn_card", label: "SSN Card", icon: FileBadge },
  { key: "gov_id", label: "Government ID", icon: IdCard },
  { key: "utility_bill", label: "Utility Bill", icon: ReceiptText },
  { key: "other", label: "Other", icon: FileText },
];
const DOC_LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.key, d.label]));
const SCORE_COLORS = { Equifax: "#EF4444", Experian: "#3B82F6", TransUnion: "#06B6D4" };

export default function ConsumerDashboard({ consumerId, onBack }) {
  const { apiErr, user } = useAuth();
  const isOwner = user?.role !== "staff";
  const isViewOnly = user?.permission === "view";
  const [assignOpen, setAssignOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [shareWith, setShareWith] = useState([]);
  const [d, setD] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [reqType, setReqType] = useState("ssn_card");
  const [reqNote, setReqNote] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [scoreForm, setScoreForm] = useState({ bureau: "Equifax", score: "", date: "" });
  const [savingScore, setSavingScore] = useState(false);
  const [docType, setDocType] = useState("ssn_card");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/consumers/${consumerId}/dashboard`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [consumerId]);

  const addScore = async () => {
    if (!scoreForm.score) { toast.error("Enter a score"); return; }
    setSavingScore(true);
    try {
      await api.post(`/consumers/${consumerId}/scores`, { bureau: scoreForm.bureau, score: Number(scoreForm.score), date: scoreForm.date || undefined });
      toast.success("Score logged"); setScoreForm({ bureau: "Equifax", score: "", date: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingScore(false); }
  };

  const delScore = async (id) => { await api.delete(`/scores/${id}`); load(); };

  const uploadDoc = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f); fd.append("docType", docType);
    try {
      await api.post(`/consumers/${consumerId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${DOC_LABEL[docType]} uploaded`); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const delDoc = async (id) => { await api.delete(`/documents/${id}`); toast.success("Removed"); load(); };
  const requestDoc = async () => {
    setReqBusy(true);
    try {
      await api.post(`/consumers/${consumerId}/doc-requests`, { docType: reqType, note: reqNote });
      toast.success("Document request sent to client"); setReqNote(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setReqBusy(false); }
  };
  const cancelReq = async (id) => { await api.delete(`/doc-requests/${id}`); load(); };
  const makeDispute = async (tid) => {
    try { await api.post(`/consumers/${consumerId}/tradelines/${tid}/dispute`); toast.success("Dispute created & added to letters"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const disputeAllFraud = async () => {
    try {
      const { data } = await api.post(`/consumers/${consumerId}/tradelines/dispute-fraud`);
      toast.success(data.created > 0 ? `${data.created} dispute(s) created from fraud-flagged accounts` : "No new fraud-flagged accounts to dispute");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const requestType = async (type) => {
    try { await api.post(`/consumers/${consumerId}/doc-requests`, { docType: type, note: "" }); toast.success("Request sent to client"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const viewFile = (path) => window.open(`${API}/files/${path}?auth=${getToken()}`, "_blank");
  const invite = async () => {
    try { const { data } = await api.post(`/consumers/${consumerId}/invite`); toast.success(`Invite emailed to ${data.email}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const packet = () => window.open(`${API}/consumers/${consumerId}/packet.pdf?auth=${getToken()}`, "_blank");
  const openAssign = async () => {
    try { const { data } = await api.get("/staff"); setTeam(data.members || []); } catch {}
    setAssignee(c.created_by || user?.id || "");
    setShareWith(c.shared_with || []);
    setAssignOpen(true);
  };
  const toggleShare = (id) => setShareWith((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const saveAssign = async () => {
    try {
      await api.put(`/consumers/${consumerId}/assign`, { assigneeId: assignee, sharedWith: shareWith.filter((x) => x !== assignee) });
      toast.success("Client reassigned"); setAssignOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>;

  const c = d.consumer;
  const openEdit = () => {
    setEdit({ fullName: c.fullName || "", email: c.email || "", phone: c.phone || "", address: c.address || "",
      ssnLast4: c.ssnLast4 || "", dob: c.dob || "", ftcReportId: c.ftcReportId || "",
      policeReportNumber: c.policeReportNumber || "", status: c.status || "New Intake", notes: c.notes || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!edit.fullName?.trim()) { toast.error("Full name is required"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/consumers/${consumerId}`, edit);
      toast.success("Consumer updated"); setEditOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };
  const setE = (k) => (e) => setEdit((p) => ({ ...p, [k]: e.target.value }));
  const chartData = (() => {
    const byDate = {};
    d.scores.forEach((s) => { byDate[s.date] = { ...(byDate[s.date] || { date: s.date }), [s.bureau]: s.score }; });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  return (
    <div className="space-y-6" data-testid="consumer-dashboard">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#C1121F] transition-colors" data-testid="dashboard-back-button">
        <ArrowLeft className="h-4 w-4" /> Back to consumers
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 rounded-xl bg-[#C1121F]/15 grid place-items-center font-display font-bold text-xl text-[#9B0E18]">
            {c.fullName.split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </span>
          <div>
            <h2 className="font-display text-2xl font-extrabold">{c.fullName}</h2>
            <div className="text-sm text-neutral-500">{c.email || "—"} · {c.phone || "—"}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded-md bg-neutral-50 border border-neutral-200 text-neutral-600">SSN •{c.ssnLast4 || "----"}</span>
              <span className="px-2 py-0.5 rounded-md bg-neutral-50 border border-neutral-200 text-neutral-600">DOB {c.dob || "—"}</span>
              {c.ftcReportId && <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-700">FTC {c.ftcReportId}</span>}
              <span className="px-2 py-0.5 rounded-md bg-neutral-50 border border-neutral-200 text-neutral-600">{c.status}</span>
              {c.assigneeName && <span className="px-2 py-0.5 rounded-md bg-[#C1121F]/[0.06] border border-[#C1121F]/20 text-[#9B0E18] inline-flex items-center gap-1" data-testid="assignee-badge"><UserCog className="h-3 w-3" /> {c.assigneeName}{c.sharedNames?.length ? ` +${c.sharedNames.length}` : ""}</span>}
              {d.stats.readyToFile
                ? <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-200 text-emerald-700 inline-flex items-center gap-1" data-testid="readiness-badge"><CheckCircle2 className="h-3 w-3" /> Ready to file</span>
                : <span className="px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-200 text-amber-700 inline-flex items-center gap-1" data-testid="readiness-badge"><AlertTriangle className="h-3 w-3" /> Needs: {d.stats.missing.join(", ")}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <Button onClick={openAssign} variant="outline" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="assign-consumer-button">
              <UserCog className="h-4 w-4 mr-1.5" /> Assign
            </Button>
          )}
          {!isViewOnly && (
            <Button onClick={openEdit} variant="outline" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="edit-consumer-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          )}
          <Button onClick={invite} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="invite-consumer-button">
            <Mail className="h-4 w-4 mr-1.5" /> Invite to Portal
          </Button>
          <Button onClick={packet} variant="outline" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="download-packet-button">
            <Download className="h-4 w-4 mr-1.5" /> Packet PDF
          </Button>
        </div>
      </div>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-[#F6EEEE] border-neutral-200">
          <DialogHeader><DialogTitle className="font-display">Assign this client</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-neutral-600">Primary owner</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger className="mt-1.5 bg-white border-neutral-200" data-testid="assign-owner-select"><SelectValue placeholder="Select teammate" /></SelectTrigger>
                <SelectContent>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}{m.isOwner ? " (you)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {team.filter((m) => m.id !== assignee).length > 0 && (
              <div>
                <Label className="text-neutral-600">Also share with</Label>
                <div className="mt-2 space-y-2">
                  {team.filter((m) => m.id !== assignee).map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                      <input type="checkbox" checked={shareWith.includes(m.id)} onChange={() => toggleShare(m.id)} className="accent-[#C1121F]" data-testid={`share-check-${m.id}`} />
                      {m.name || m.email}{m.isOwner ? " (you)" : ""}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveAssign} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="save-assign-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#F6EEEE] border-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Edit consumer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="text-neutral-600">Full name *</Label><Input value={edit.fullName || ""} onChange={setE("fullName")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="edit-fullname-input" /></div>
            <div><Label className="text-neutral-600">Email</Label><Input value={edit.email || ""} onChange={setE("email")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="edit-email-input" /></div>
            <div><Label className="text-neutral-600">Phone</Label><Input value={edit.phone || ""} onChange={setE("phone")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div className="col-span-2"><Label className="text-neutral-600">Address</Label><Input value={edit.address || ""} onChange={setE("address")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div><Label className="text-neutral-600">SSN (last 4)</Label><Input value={edit.ssnLast4 || ""} onChange={setE("ssnLast4")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div><Label className="text-neutral-600">Date of birth</Label><Input value={edit.dob || ""} onChange={setE("dob")} placeholder="1990-01-01" className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div><Label className="text-neutral-600">FTC report #</Label><Input value={edit.ftcReportId || ""} onChange={setE("ftcReportId")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div><Label className="text-neutral-600">Police report #</Label><Input value={edit.policeReportNumber || ""} onChange={setE("policeReportNumber")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
            <div className="col-span-2">
              <Label className="text-neutral-600">Status</Label>
              <Select value={edit.status} onValueChange={(v) => setEdit((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-1.5 bg-[#FFFFFF] border-neutral-200" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EDIT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-neutral-600">Notes</Label><Textarea value={edit.notes || ""} onChange={setE("notes")} className="mt-1.5 bg-[#FFFFFF] border-neutral-200" /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={savingEdit} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="save-consumer-edit-button">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Total Disputed" value={d.stats.totalDisputed} accent="#60A5FA" icon={TrendingUp} />
        <Tile label="Active Disputes" value={d.stats.active} accent="#F59E0B" icon={CalendarClock} />
        <Tile label="Blocked / Resolved" value={d.stats.resolved} accent="#15803D" icon={ShieldCheck} />
        <Tile label="Documents" value={d.documents.length} accent="#C1121F" icon={FileText} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Credit scores */}
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Credit Scores</h3></div>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(d.stats.latestScores).map(([b, s]) => (
              <span key={b} data-testid="latest-score-chip" className="px-3 py-1.5 rounded-lg border text-sm font-mono" style={{ borderColor: `${SCORE_COLORS[b]}66`, color: SCORE_COLORS[b] }}>{b}: <b>{s}</b></span>
            ))}
            {Object.keys(d.stats.latestScores).length === 0 && <span className="text-sm text-neutral-400">No scores logged yet.</span>}
          </div>
          {chartData.length > 0 && (
            <div className="h-48 -ml-2" style={{ minHeight: 192 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff14" />
                  <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                  <YAxis domain={[300, 850]} stroke="#94A3B8" fontSize={11} width={34} />
                  <Tooltip contentStyle={{ background: "#F6EEEE", border: "1px solid #ffffff22", borderRadius: 8, fontSize: 12 }} />
                  {BUREAUS.map((b) => <Line key={b} type="monotone" dataKey={b} stroke={SCORE_COLORS[b]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-4 grid grid-cols-4 gap-2 items-end">
            <div className="col-span-1">
              <Label className="text-[11px] text-neutral-500">Bureau</Label>
              <Select value={scoreForm.bureau} onValueChange={(v) => setScoreForm({ ...scoreForm, bureau: v })}>
                <SelectTrigger className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200 text-xs" data-testid="score-bureau-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1"><Label className="text-[11px] text-neutral-500">Score</Label><Input value={scoreForm.score} onChange={(e) => setScoreForm({ ...scoreForm, score: e.target.value })} className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200" placeholder="640" data-testid="score-value-input" /></div>
            <div className="col-span-1"><Label className="text-[11px] text-neutral-500">Date</Label><Input type="date" value={scoreForm.date} onChange={(e) => setScoreForm({ ...scoreForm, date: e.target.value })} className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200 text-xs" data-testid="score-date-input" /></div>
            <Button onClick={addScore} disabled={savingScore} className="h-9 rounded-lg bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="add-score-button">
              {savingScore ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>
          {d.scores.length > 0 && (
            <div className="mt-3 space-y-1 max-h-28 overflow-y-auto">
              {[...d.scores].reverse().map((s) => (
                <div key={s.id} className="flex items-center justify-between text-xs text-neutral-500 px-2 py-1 rounded bg-neutral-50">
                  <span>{s.date}</span><span style={{ color: SCORE_COLORS[s.bureau] }}>{s.bureau}</span><span className="font-mono text-neutral-800">{s.score}</span>
                  <button onClick={() => delScore(s.id)} className="text-neutral-400 hover:text-rose-400"><Trash className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Identity documents */}
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="flex items-center gap-2 mb-4"><FileBadge className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Identity Documents</h3></div>
          <div className="flex gap-2 items-end mb-4">
            <div className="flex-1">
              <Label className="text-[11px] text-neutral-500">Document type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200 text-sm" data-testid="document-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <input ref={fileRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadDoc} data-testid="document-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-9 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="upload-document-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UploadCloud className="h-4 w-4 mr-1.5" /> Upload</>}
            </Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {d.documents.length === 0 && <p className="text-sm text-neutral-400">No identity documents uploaded yet. Add the consumer's SSN card, ID and a utility bill.</p>}
            {d.documents.map((doc) => {
              const T = DOC_TYPES.find((t) => t.key === doc.docType) || (doc.docType === "credit_report" ? { label: "Credit Report", icon: FileText } : DOC_TYPES[3]);
              return (
                <div key={doc.id} className="flex items-center gap-3 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2" data-testid="document-item">
                  <T.icon className="h-4 w-4 text-[#C1121F]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{doc.original_filename}</div>
                    <div className="text-[11px] text-neutral-400">{DOC_LABEL[doc.docType] || (doc.docType === "credit_report" ? "Credit Report" : "Document")}</div>
                  </div>
                  <button onClick={() => viewFile(doc.storage_path)} className="text-xs text-[#C1121F] hover:text-[#9B0E18]">View</button>
                  <button onClick={() => delDoc(doc.id)} className="text-neutral-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Client account review */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6" data-testid="tradeline-review-section">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Client Account Review</h3></div>
          {(d.tradelines || []).some((t) => t.clientDecision === "fraud" && !t.disputeId) && (
            <Button onClick={disputeAllFraud} className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold h-8 text-xs px-3" data-testid="dispute-all-fraud-button">
              Dispute all fraud-flagged
            </Button>
          )}
        </div>
        {(d.tradelines || []).length === 0 ? (
          <p className="text-sm text-neutral-400">No detected accounts yet. Upload a credit report to populate this list, then the client flags which are fraud.</p>
        ) : (
          <div className="space-y-2">
            {d.tradelines.map((t) => {
              const bc = BUREAU_COLORS[t.bureau] || BUREAU_COLORS.Furnisher;
              const badge = t.clientDecision === "fraud" ? { c: "bg-rose-50 text-rose-700 border-rose-200", l: "Client: FRAUD" }
                : t.clientDecision === "mine" ? { c: "bg-emerald-50 text-emerald-700 border-emerald-200", l: "Client: Mine" }
                : { c: "bg-stone-100 text-stone-600 border-stone-300", l: "Awaiting client" };
              return (
                <div key={t.id} className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="tradeline-row">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`px-2 py-0.5 rounded-md border text-xs ${bc.chip} ${bc.text} ${bc.border}`}>{t.bureau}</span>
                    <span className="font-medium truncate">{t.creditor}</span>
                    <span className="text-xs text-neutral-400">{t.negativeType} · {t.balance}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-md border ${badge.c}`}>{badge.l}</span>
                    {t.disputeId ? <span className="text-xs text-neutral-400">Dispute created</span>
                      : (t.clientDecision === "fraud" && <Button onClick={() => makeDispute(t.id)} className="h-7 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] text-xs font-semibold px-3" data-testid="create-dispute-from-tradeline">Create Dispute</Button>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Standard docs checklist */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6" data-testid="docs-checklist-section">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Standard Documents Checklist</h3></div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[{ key: "ssn_card", label: "SSN Card", icon: FileBadge }, { key: "gov_id", label: "Government ID", icon: IdCard }, { key: "utility_bill", label: "Utility Bill", icon: ReceiptText }].map((s) => {
            const have = (d.documents || []).some((doc) => doc.docType === s.key);
            const pending = (d.docRequests || []).some((r) => r.docType === s.key && r.status !== "Fulfilled");
            return (
              <div key={s.key} className={`rounded-xl border p-4 ${have ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid={`checklist-${s.key}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-sm font-medium"><s.icon className="h-4 w-4 text-neutral-600" /> {s.label}</span>
                  {have ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                </div>
                <div className="mt-2 text-xs">
                  {have ? <span className="text-emerald-700">On file</span>
                    : pending ? <span className="text-amber-700">Requested — awaiting client</span>
                    : <button onClick={() => requestType(s.key)} className="text-[#C1121F] hover:text-[#9B0E18] font-medium" data-testid={`checklist-request-${s.key}`}>Outstanding · Request from client</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Document requests */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6" data-testid="doc-requests-section">
        <div className="flex items-center gap-2 mb-4"><FileBadge className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Request a Document from the Client</h3></div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="w-full sm:w-48">
            <Label className="text-[11px] text-neutral-500">Document type</Label>
            <Select value={reqType} onValueChange={setReqType}>
              <SelectTrigger className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200 text-sm" data-testid="request-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="text-[11px] text-neutral-500">Note (optional)</Label>
            <Input value={reqNote} onChange={(e) => setReqNote(e.target.value)} placeholder="e.g. dated within last 60 days" className="mt-1 h-9 bg-[#F6EEEE] border-neutral-200" data-testid="request-doc-note" />
          </div>
          <Button onClick={requestDoc} disabled={reqBusy} className="h-9 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="request-doc-button">
            {reqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request"}
          </Button>
        </div>
        {(d.docRequests || []).length > 0 && (
          <div className="mt-4 space-y-2">
            {d.docRequests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-sm" data-testid="doc-request-item">
                <div><span className="font-medium">{r.label}</span>{r.note ? <span className="text-neutral-400"> · {r.note}</span> : null}</div>
                <div className="flex items-center gap-3">
                  {r.status === "Fulfilled"
                    ? <span className="text-xs px-2 py-0.5 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200">Fulfilled</span>
                    : <span className="text-xs px-2 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200">Requested</span>}
                  {r.status !== "Fulfilled" && <button onClick={() => cancelReq(r.id)} className="text-neutral-400 hover:text-rose-400"><Trash className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Letters & dates */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
        <div className="flex items-center gap-2 mb-4"><CalendarClock className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Disputes &amp; Letter Dates</h3></div>
        {d.lettersTimeline.length === 0 ? <p className="text-sm text-neutral-400">No letters yet. Create disputes from an uploaded report or the Letter Studio.</p> : (
          <div className="space-y-3">
            {d.lettersTimeline.map((l, i) => {
              const bc = BUREAU_COLORS[l.bureau] || BUREAU_COLORS.Furnisher;
              return (
                <div key={i} className="rounded-xl bg-neutral-50 border border-neutral-200 p-4" data-testid="letter-timeline-row">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md border text-xs ${bc.chip} ${bc.text} ${bc.border}`}>{l.bureau}</span>
                      <span className="text-sm font-medium">{l.creditor || l.type}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-md border ${STATUS_COLORS[l.status] || "bg-stone-100 text-stone-600 border-stone-300"}`}>{l.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500">
                    <span>Sent: <span className="text-neutral-800 font-mono">{l.date || "—"}</span></span>
                    <span>§605B deadline: <span className="text-amber-700 font-mono">{l.deadline || "—"}</span></span>
                  </div>
                  {l.events?.length > 0 && (
                    <div className="mt-2 border-l-2 border-[#C1121F]/40 pl-3 space-y-1">
                      {l.events.map((ev, j) => (
                        <div key={j} className="text-[11px] text-neutral-400"><span className="text-neutral-600">{ev.stage}</span> — {new Date(ev.date).toLocaleDateString()} · {ev.note}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Activity log */}
      <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6" data-testid="activity-log-section">
        <div className="flex items-center gap-2 mb-4"><History className="h-5 w-5 text-[#C1121F]" /><h3 className="font-display font-bold">Activity Log</h3></div>
        {(!d.activity || d.activity.length === 0) ? (
          <p className="text-sm text-neutral-400">No activity recorded yet.</p>
        ) : (
          <div className="relative pl-4">
            <div className="absolute left-1 top-1 bottom-1 w-px bg-neutral-100" />
            <div className="space-y-4">
              {d.activity.map((a) => (
                <div key={a.id} className="relative" data-testid="activity-log-item">
                  <span className="absolute -left-[13px] top-1 h-2.5 w-2.5 rounded-full bg-[#C1121F] ring-4 ring-[#FFFFFF]" />
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-neutral-800">{a.action}</span>
                    <span className="text-[11px] text-neutral-400 font-mono">{new Date(a.at).toLocaleString()}</span>
                  </div>
                  {a.detail && <div className="text-xs text-neutral-500">{a.detail}</div>}
                  <div className="text-[11px] text-neutral-400">by {a.actor}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-5">
      <span className="inline-grid place-items-center h-10 w-10 rounded-xl" style={{ backgroundColor: `${accent}1f` }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </span>
      <div className="mt-4 font-display text-3xl font-extrabold">{value}</div>
      <div className="text-sm text-neutral-500">{label}</div>
    </div>
  );
}
